import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  X,
  Upload,
  AlertTriangle,
  Loader2,
  RotateCcw,
  FileText,
} from 'lucide-react';

import { cn } from '../lib/utils';
import { SuggestionItem, RoomCompletenessReport, ParsedEstimateResponse, ParsedLineItem, ParserDiagnostics, Provenance } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { BackendAPI, DictionarySearchResult } from '../lib/api';

// ================================================================= //
//          Upload Area Component                                      //
// ================================================================= //

interface UploadAreaProps {
  onFileSelect: (file: File) => void;
  isLoading: boolean;
  selectedFile: File | null;
  onAnalyze: () => void;
}

export const UploadArea: React.FC<UploadAreaProps> = ({
  onFileSelect,
  isLoading,
  selectedFile,
  onAnalyze,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputId = 'completeness-upload-input';

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && isValidFile(file)) onFileSelect(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && isValidFile(file)) onFileSelect(file);
  };

  const isValidFile = (file: File): boolean => {
    const valid = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    return valid.includes(file.type) || file.name.endsWith('.xlsx') || file.name.endsWith('.pdf');
  };

  return (
    <div className="space-y-4">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors',
          isDragOver ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400',
          isLoading && 'pointer-events-none opacity-60'
        )}
      >
        <Upload className="mb-3 h-10 w-10 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">
          Drag and drop your estimate file here
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Supports PDF and Excel (.xlsx) files
        </p>
        <label
          htmlFor={inputId}
          className={cn(
            'mt-4 inline-flex cursor-pointer rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90',
            isLoading && 'cursor-not-allowed opacity-50'
          )}
        >
          Choose File
        </label>
        <input
          id={inputId}
          type="file"
          accept=".pdf,.xlsx,.xls"
          disabled={isLoading}
          onChange={(e) => {
            handleFileInput(e);
            // Reset so selecting the same file twice still triggers onChange
            e.target.value = '';
          }}
          // Reason: keep the input in layout flow so Chrome's hidden-input
          // heuristics don't block the file dialog. Visually invisible via
          // opacity:0 + 0x0 hit-area; the label's htmlFor opens the picker.
          style={{
            position: 'absolute',
            width: 0,
            height: 0,
            opacity: 0,
            pointerEvents: 'none',
          }}
        />
      </div>

      {selectedFile && (
        <div className="flex items-center justify-between rounded-md border bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <span className="text-sm font-medium">{selectedFile.name}</span>
            <span className="text-xs text-muted-foreground">
              ({(selectedFile.size / 1024).toFixed(1)} KB)
            </span>
          </div>
          <button
            onClick={onAnalyze}
            disabled={isLoading}
            className="flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Importing...
              </>
            ) : (
              'Import'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// ================================================================= //
//          Aggressiveness Control                                     //
// ================================================================= //

interface AggressivenessControlProps {
  value: string;
  onChange: (v: string) => void;
}

const aggressivenessOptions = [
  { key: 'conservative', label: 'Conservative', desc: 'Rules only' },
  { key: 'moderate', label: 'Moderate', desc: 'Rules + common patterns' },
  { key: 'aggressive', label: 'Aggressive', desc: 'All patterns' },
];

export const AggressivenessControl: React.FC<AggressivenessControlProps> = ({
  value,
  onChange,
}) => (
  <div>
    <label className="mb-2 block text-sm font-medium text-foreground">Suggestion Depth</label>
    <div className="flex gap-2">
      {aggressivenessOptions.map((opt) => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          className={cn(
            'flex-1 rounded-md border px-3 py-2 text-center transition-colors',
            value === opt.key
              ? 'border-primary bg-primary/10 text-primary font-medium'
              : 'border-gray-200 hover:bg-slate-50'
          )}
        >
          <p className="text-sm">{opt.label}</p>
          <p className="text-xs text-muted-foreground">{opt.desc}</p>
        </button>
      ))}
    </div>
  </div>
);

// ================================================================= //
//          Claim Type Selector                                        //
// ================================================================= //

interface ClaimTypeSelectorProps {
  value: string | null;
  onChange: (v: string | null) => void;
}

const claimTypes = ['Auto-detect', 'Water', 'Fire', 'Wind', 'Flood', 'Hurricane', 'Hail', 'Other'];

export const ClaimTypeSelector: React.FC<ClaimTypeSelectorProps> = ({ value, onChange }) => (
  <div>
    <label className="mb-2 block text-sm font-medium text-foreground">Claim Type</label>
    <select
      value={value ?? 'Auto-detect'}
      onChange={(e) => onChange(e.target.value === 'Auto-detect' ? null : e.target.value)}
      className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
    >
      {claimTypes.map((ct) => (
        <option key={ct} value={ct}>{ct}</option>
      ))}
    </select>
  </div>
);

// ================================================================= //
//          Suggestion Row                                             //
// ================================================================= //

interface SuggestionRowProps {
  suggestion: SuggestionItem;
  onDismiss: (id: string) => void;
  isDismissed: boolean;
  onRecover?: (id: string) => void;
}

const tierStyles: Record<string, { bg: string; border: string; badge: string; label: string }> = {
  required: {
    bg: 'bg-green-50',
    border: 'border-l-4 border-green-500',
    badge: 'bg-green-100 text-green-800',
    label: 'Required',
  },
  recommended: {
    bg: 'bg-yellow-50',
    border: 'border-l-4 border-yellow-500',
    badge: 'bg-yellow-100 text-yellow-800',
    label: 'Recommended',
  },
  consider: {
    bg: 'bg-blue-50',
    border: 'border-l-4 border-blue-500',
    badge: 'bg-blue-100 text-blue-800',
    label: 'Consider',
  },
};

export const SuggestionRow: React.FC<SuggestionRowProps> = ({
  suggestion,
  onDismiss,
  isDismissed,
  onRecover,
}) => {
  const style = tierStyles[suggestion.tier] || tierStyles.consider;

  if (isDismissed) {
    return (
      <div className="flex items-center justify-between border-b px-4 py-2 bg-gray-50 opacity-60">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-muted-foreground line-through">
            {suggestion.xactimate_code}
          </span>
          <span className="text-xs text-muted-foreground line-through">
            {suggestion.description}
          </span>
        </div>
        {onRecover && (
          <button
            onClick={() => onRecover(suggestion.suggestion_id)}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-primary hover:bg-primary/10"
          >
            <RotateCcw className="h-3 w-3" />
            Recover
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn('flex items-center gap-3 border-b px-4 py-3', style.bg, style.border)}
      title={suggestion.provenance_reason || undefined}
    >
      {/* Tier badge */}
      <span className={cn('inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', style.badge)}>
        {style.label}
      </span>

      {/* Code */}
      <span className="shrink-0 font-mono text-xs text-primary">{suggestion.xactimate_code}</span>

      {/* Description */}
      <span className="flex-1 text-sm text-foreground">{suggestion.description}</span>

      {/* Provenance pill — always ADDED for suggestions, hover reveals reason */}
      {suggestion.provenance && (
        <ProvenancePill
          provenance={suggestion.provenance}
          label={suggestion.provenance_reason}
        />
      )}

      {/* Source badge */}
      <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground uppercase">
        {suggestion.source}
      </span>

      {/* Citation tooltip */}
      {suggestion.citation && (
        <span
          title={`${suggestion.citation.source_type}: ${suggestion.citation.summary}`}
          className="cursor-help text-muted-foreground"
        >
          <AlertTriangle className="h-3.5 w-3.5" />
        </span>
      )}

      {/* Warning */}
      {suggestion.warning && (
        <span
          title={suggestion.warning}
          className="cursor-help text-amber-600"
        >
          <AlertTriangle className="h-3.5 w-3.5" />
        </span>
      )}

      {/* Dismiss */}
      <button
        onClick={() => onDismiss(suggestion.suggestion_id)}
        className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-100 hover:text-red-600"
        title="Dismiss suggestion"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// ================================================================= //
//          Room Section                                                //
// ================================================================= //

interface RoomSectionProps {
  room: RoomCompletenessReport;
  onDismiss: (id: string) => void;
  onRecover: (id: string) => void;
  dismissedIds: Set<string>;
}

export const RoomSection: React.FC<RoomSectionProps> = ({
  room,
  onDismiss,
  onRecover,
  dismissedIds,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [showDismissed, setShowDismissed] = useState(false);

  const activeSuggestions = room.suggestions.filter((s) => !dismissedIds.has(s.suggestion_id));
  const dismissedSuggestions = room.suggestions.filter((s) => dismissedIds.has(s.suggestion_id));

  return (
    <div className="rounded-lg border bg-white shadow-sm">
      {/* Room header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between p-4 text-left hover:bg-slate-50"
      >
        <div className="flex items-center gap-3">
          {isOpen ? (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          )}
          <div>
            <h3 className="text-lg font-semibold text-foreground">{room.room_name}</h3>
            {room.room_type && (
              <span className="text-xs text-muted-foreground">{room.room_type}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{room.existing_items.length} items</span>
          {activeSuggestions.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
              +{activeSuggestions.length} suggestions
            </span>
          )}
        </div>
      </button>

      {/* Room warnings */}
      {isOpen && room.warnings.length > 0 && (
        <div className="border-t bg-amber-50 px-4 py-2">
          {room.warnings.map((w, i) => (
            <p key={i} className="flex items-center gap-2 text-xs text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {w}
            </p>
          ))}
        </div>
      )}

      {/* Items and suggestions */}
      {isOpen && (
        <div className="border-t">
          {/* Table header — shared with verify-parse view */}
          <ScopeTableHeader />

          {/* Existing items — shared row component. The backend serializes
              category, selector, and activity as separate fields on the
              pydantic LineItem model (xactimate_code is a non-serializing
              @property), so prefer those. Fall back to splitting
              xactimate_code only if the separate fields are missing.
              The stale "No code match" subtitle is intentionally removed per
              PRPs/verify-parse-useful-output.md line 70 — the amber row
              background + em-dash CAT/SEL already communicate unmapped. */}
          {room.existing_items.map((item, i) => {
            const fallback = splitXactimateCode(item.xactimate_code || '');
            return (
              <React.Fragment key={i}>
                {/* B-039: bold section headings parsed from the PDF, before this item */}
                {headingsBefore(room.headings, i)}
                <ScopeLineItemRow
                  lineNumber={item.line_number}
                  category={item.category || fallback.cat}
                  selector={item.selector || fallback.sel}
                  activity={item.activity}
                  quantity={item.quantity}
                  unit={item.unit}
                  description={item.description}
                  provenance={item.provenance}
                  provenanceReason={item.provenance_reason}
                  notes={item.notes}
                />
              </React.Fragment>
            );
          })}
          {/* Any trailing headings positioned after the last item */}
          {headingsBefore(room.headings, room.existing_items.length)}

          {/* Active suggestions */}
          {activeSuggestions.map((suggestion) => (
            <SuggestionRow
              key={suggestion.suggestion_id}
              suggestion={suggestion}
              onDismiss={onDismiss}
              isDismissed={false}
            />
          ))}

          {/* Dismissed suggestions toggle */}
          {dismissedSuggestions.length > 0 && (
            <div className="border-t bg-gray-50 px-4 py-2">
              <button
                onClick={() => setShowDismissed(!showDismissed)}
                className="text-xs font-medium text-primary hover:underline"
              >
                {showDismissed ? 'Hide' : 'Show'} {dismissedSuggestions.length} dismissed
              </button>
            </div>
          )}

          {/* Dismissed suggestions */}
          {showDismissed &&
            dismissedSuggestions.map((suggestion) => (
              <SuggestionRow
                key={suggestion.suggestion_id}
                suggestion={suggestion}
                onDismiss={onDismiss}
                isDismissed={true}
                onRecover={onRecover}
              />
            ))}

          {/* Damage type info */}
          {room.damage_type_inferred && (
            <div className="border-t bg-slate-50 px-4 py-2">
              <p className="text-xs text-muted-foreground">
                Damage type: <span className="font-medium">{room.damage_type_inferred}</span>
                {room.damage_type_source && (
                  <span className="ml-1">({room.damage_type_source})</span>
                )}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ================================================================= //
//          Out of Coverage Section                                     //
// ================================================================= //

interface OutOfCoverageSectionProps {
  items: SuggestionItem[];
  claimType: string;
}

export const OutOfCoverageSection: React.FC<OutOfCoverageSectionProps> = ({
  items,
  claimType,
}) => {
  if (items.length === 0) return null;

  return (
    <Card className="border-gray-300 bg-gray-50">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg text-muted-foreground">
          Items Outside {claimType} Coverage
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-muted-foreground italic">
          These items were identified but may not be covered under a {claimType} claim.
        </p>
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={item.suggestion_id}
              className="flex items-center gap-3 rounded-md border border-gray-200 bg-white p-3"
            >
              <span className="font-mono text-xs text-muted-foreground">
                {item.xactimate_code}
              </span>
              <span className="flex-1 text-sm italic text-muted-foreground">
                {item.description}
              </span>
              {item.warning && (
                <span className="text-xs text-amber-600">{item.warning}</span>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

// ================================================================= //
//          Parse Warning Banner                                        //
// ================================================================= //

// ================================================================= //
//          Parser Diagnostics Banner                                  //
// ================================================================= //

interface ParserDiagnosticsBannerProps {
  diagnostics: ParserDiagnostics;
}

type DiagnosticsTone = 'green' | 'amber' | 'red';

function classifyDiagnostics(d: ParserDiagnostics): DiagnosticsTone {
  const lineItems = d.line_items_total ?? d.line_items_emitted ?? 0;
  if (lineItems === 0 || d.confidence < 0.5) return 'red';
  if (d.confidence < 0.85 || (d.warnings && d.warnings.length > 0)) return 'amber';
  return 'green';
}

const TONE_STYLES: Record<DiagnosticsTone, { border: string; bg: string; text: string; accent: string }> = {
  green: {
    border: 'border-green-200',
    bg: 'bg-green-50',
    text: 'text-green-900',
    accent: 'text-green-700',
  },
  amber: {
    border: 'border-amber-200',
    bg: 'bg-amber-50',
    text: 'text-amber-900',
    accent: 'text-amber-700',
  },
  red: {
    border: 'border-red-200',
    bg: 'bg-red-50',
    text: 'text-red-900',
    accent: 'text-red-700',
  },
};

/**
 * Color-coded banner summarizing how the PDF/Excel parse went. Renders
 * above the line-items table so the estimator sees confidence and
 * coverage at a glance, with an expandable JSON view + warnings list
 * for deeper inspection.
 */
export const ParserDiagnosticsBanner: React.FC<ParserDiagnosticsBannerProps> = ({
  diagnostics,
}) => {
  const [expanded, setExpanded] = useState(false);
  const tone = classifyDiagnostics(diagnostics);
  const styles = TONE_STYLES[tone];
  const lineItems = diagnostics.line_items_total ?? diagnostics.line_items_emitted ?? 0;
  const confidencePct = Math.round((diagnostics.confidence ?? 0) * 100);
  const pagesWithItems = diagnostics.pages_with_line_items ?? 0;
  const pagesTotal = diagnostics.pages_total ?? 0;
  const preamble = diagnostics.pages_skipped_as_preamble ?? 0;
  const unprocessable = diagnostics.pages_unprocessable ?? 0;
  const warnings = diagnostics.warnings ?? [];

  // Specific call-to-action when a text-based strategy returned no items
  // AND the cheap qty+unit heuristic also found nothing on any page —
  // strongly suggests a scanned PDF we can't currently read. If the
  // heuristic DID find rows but the full parser produced zero, that's a
  // layout-mismatch (not scanning) and gets a different message via
  // `layoutMismatch` below.
  const scannedFallback =
    lineItems === 0
    && (diagnostics.strategy ?? '').includes('text_layer')
    && pagesWithItems === 0;
  const layoutMismatch =
    lineItems === 0
    && (diagnostics.strategy ?? '').includes('text_layer')
    && pagesWithItems > 0;

  // One-line summary, e.g.
  //   "Parsed via text_layer · confidence 92% · 47 line items from 8 of 12
  //    pages · 3 pages skipped as preamble"
  const summaryParts = [
    `Parsed via ${diagnostics.strategy || 'unknown'}`,
    `confidence ${confidencePct}%`,
    pagesTotal > 0
      ? `${lineItems} line items from ${pagesWithItems} of ${pagesTotal} pages`
      : `${lineItems} line items`,
  ];
  if (preamble > 0) {
    summaryParts.push(
      `${preamble} ${preamble === 1 ? 'page' : 'pages'} skipped as preamble`
    );
  }
  if (unprocessable > 0) {
    summaryParts.push(
      `${unprocessable} unprocessable ${unprocessable === 1 ? 'page' : 'pages'}`
    );
  }

  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        styles.border,
        styles.bg,
        styles.text
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className={cn('mt-0.5 h-5 w-5 shrink-0', styles.accent)} />
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium">{summaryParts.join(' · ')}</p>

          {scannedFallback && (
            <p className="rounded border border-red-300 bg-red-100 px-3 py-2 text-sm font-medium text-red-900">
              This appears to be a scanned PDF. Image-based parsing is in
              development — for now, you can manually enter line items below.
            </p>
          )}

          {layoutMismatch && (
            <p className="rounded border border-red-300 bg-red-100 px-3 py-2 text-sm font-medium text-red-900">
              Found line-item patterns on {pagesWithItems} of {pagesTotal} pages
              but couldn't extract them — this layout isn't recognized by the
              current parser. For now, you can manually enter line items below.
            </p>
          )}

          {warnings.length > 0 && (
            <ul className={cn('list-disc pl-5 text-xs space-y-0.5', styles.accent)}>
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}

          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className={cn(
              'flex items-center gap-1 text-xs font-medium underline-offset-2 hover:underline',
              styles.accent
            )}
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {expanded ? 'Hide details' : 'Show details'}
          </button>

          {expanded && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-4">
                <DiagnosticStat label="CAT populated" value={pctLabel(diagnostics.pct_cat_populated)} />
                <DiagnosticStat label="SEL populated" value={pctLabel(diagnostics.pct_sel_populated)} />
                <DiagnosticStat label="Activity populated" value={pctLabel(diagnostics.pct_activity_populated)} />
                <DiagnosticStat label="Unit populated" value={pctLabel(diagnostics.pct_unit_populated)} />
              </div>
              <pre className="overflow-x-auto rounded bg-white/60 p-2 text-[11px] leading-snug">
                {JSON.stringify(diagnostics, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DiagnosticStat: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex items-center justify-between gap-2">
    <span className="opacity-70">{label}</span>
    <span className="font-mono font-medium">{value}</span>
  </div>
);

function pctLabel(value: number | undefined): string {
  if (value === undefined || value === null) return '—';
  return `${Math.round(value * 100)}%`;
}

interface ParseWarningBannerProps {
  warnings: string[];
  matchStats: { matched: number; unmatched: number; total: number };
}

export const ParseWarningBanner: React.FC<ParseWarningBannerProps> = ({
  warnings,
  matchStats,
}) => {
  if (warnings.length === 0 && matchStats.unmatched === 0) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-amber-800">
            {matchStats.matched} of {matchStats.total} items matched to Xactimate codes
            {matchStats.unmatched > 0 && (
              <span className="ml-1 text-amber-600">
                ({matchStats.unmatched} unmatched)
              </span>
            )}
          </p>
          {warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-700">{w}</p>
          ))}
        </div>
      </div>
    </div>
  );
};

// ================================================================= //
//          Parse Verification View                                    //
// ================================================================= //

interface ParseVerificationViewProps {
  parsed: ParsedEstimateResponse;
  /** Unused since PRP-INTAKE-1 removed "Suggest enhancements"; kept so callers compile. */
  onConfirm?: () => void;
  onReupload: () => void;
  isAnalyzing: boolean;
  onParsedUpdate?: (next: ParsedEstimateResponse) => void;
  // B-035: faithful ESX export, available from the faithful import view.
  // Undefined when no uploaded file is in scope (e.g. parse restored from
  // session) — the button is then hidden.
  onDownloadEsx?: () => void;
  esxBusy?: boolean;
  esxError?: string | null;
}

interface OverrideTarget {
  roomIndex: number;
  lineItemIndex: number;
  description: string;
}

/**
 * "Verify parse first" view. Shows each room and its line items with
 * provenance indicators (green for ORIGINAL, yellow for UNMAPPED). User
 * decides whether the parse looks right before triggering analysis.
 */
export const ParseVerificationView: React.FC<ParseVerificationViewProps> = ({
  parsed,
  onReupload,
  isAnalyzing,
  onParsedUpdate,
  onDownloadEsx,
  esxBusy,
  esxError,
}) => {
  const { estimate, match_stats } = parsed;
  const unmappedCount = match_stats.unmatched;
  const meta = estimate.metadata;
  const [overrideTarget, setOverrideTarget] = useState<OverrideTarget | null>(
    null
  );

  const [copyToast, setCopyToast] = useState<string | null>(null);

  const handleCopyCodeList = async () => {
    const { text, count } = buildCodeListText(estimate);
    try {
      await navigator.clipboard.writeText(text);
      setCopyToast(`Copied ${count} codes`);
      setTimeout(() => setCopyToast(null), 2500);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      alert(`Copy failed: ${(err as Error).message}`);
    }
  };

  const handleExportCsv = () => {
    const csv = buildEstimateCsv(estimate);
    const today = new Date().toISOString().slice(0, 10);
    const baseName = (meta.estimate_name || 'estimate').replace(/[^A-Za-z0-9_-]+/g, '_');
    const filename = `${baseName}_codes_${today}.csv`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSelectOverride = async (cat: string, sel: string) => {
    if (!overrideTarget) return;
    try {
      const updated = await BackendAPI.overrideLineItem(
        parsed.parse_hash,
        overrideTarget.roomIndex,
        overrideTarget.lineItemIndex,
        cat,
        sel,
      );
      onParsedUpdate?.(updated);
      setOverrideTarget(null);
    } catch (err) {
      console.error('Override failed:', err);
      alert(`Override failed: ${(err as Error).message}`);
    }
  };
  const totalLineItems = estimate.rooms.reduce(
    (sum, room) => sum + room.line_items.length,
    0
  );
  const manualCount = estimate.rooms.reduce(
    (sum, room) =>
      sum + room.line_items.filter((li) => li.provenance === 'MANUAL').length,
    0
  );

  const metaFields: Array<[string, string | null | undefined]> = [
    ['Insured', meta.insured_name],
    ['Property Address', meta.property_address],
    ['Claim Number', meta.claim_number],
    ['Policy Number', meta.policy_number],
    ['Date of Loss', meta.date_of_loss],
    ['Type of Loss', meta.damage_type],
    ['Price List', meta.price_list],
    ['Estimator', meta.estimator],
  ];
  const visibleMetaFields = metaFields.filter(([, v]) => v != null && v !== '');

  const diagnostics = parsed.parser_diagnostics || parsed.parse_summary;

  return (
    <div className="space-y-4">
      {diagnostics && <ParserDiagnosticsBanner diagnostics={diagnostics} />}

      {visibleMetaFields.length > 0 && (
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <h3 className="text-base font-semibold text-foreground mb-3">
            {meta.estimate_name || 'Estimate'}
          </h3>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            {visibleMetaFields.map(([label, value]) => (
              <div key={label} className="flex flex-wrap gap-x-2 text-sm">
                <dt className="text-muted-foreground min-w-[8rem]">{label}</dt>
                <dd className="font-medium text-foreground">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
        <ParseStatCard label="Rooms" value={estimate.rooms.length} />
        <ParseStatCard label="Line Items" value={totalLineItems} />
        <ParseStatCard
          label="Mapped"
          value={match_stats.matched}
          color="text-green-700"
        />
        <ParseStatCard
          label="Unmapped"
          value={match_stats.unmatched}
          color={match_stats.unmatched > 0 ? 'text-amber-700' : 'text-foreground'}
        />
        <ParseStatCard
          label="Manual"
          value={manualCount}
          color={manualCount > 0 ? 'text-teal-700' : 'text-foreground'}
        />
      </div>

      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-foreground">
              {estimate.metadata.estimate_name || 'Estimate'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {estimate.rooms.length} rooms · {match_stats.matched} mapped ·{' '}
              {unmappedCount > 0 ? (
                <span className="text-amber-700">{unmappedCount} unmapped</span>
              ) : (
                <span className="text-green-700">0 unmapped</span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {onDownloadEsx && (
              <button
                onClick={onDownloadEsx}
                disabled={esxBusy || isAnalyzing}
                className="flex items-center gap-2 rounded-md border border-primary bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                title="Export this faithful import as an importable Xactimate .ESX (no enhancement)"
              >
                {esxBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Download Faithful Scope as ESX
              </button>
            )}
            <button
              onClick={handleCopyCodeList}
              disabled={isAnalyzing}
              className="rounded-md border px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
            >
              {copyToast || 'Copy as code list'}
            </button>
            <button
              onClick={handleExportCsv}
              disabled={isAnalyzing}
              className="rounded-md border px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
            >
              Export CSV
            </button>
            <button
              onClick={onReupload}
              disabled={isAnalyzing}
              className="rounded-md border px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
            >
              Re-upload
            </button>
          </div>
        </div>
        {esxError && (
          <p className="mt-2 text-sm text-red-700">ESX export failed: {esxError}</p>
        )}
      </div>

      {estimate.rooms.map((room, roomIndex) => (
        <div key={`${room.group_code}-${roomIndex}`} className="rounded-lg border bg-white shadow-sm">
          <div className="border-b px-4 py-2.5">
            <h4 className="text-sm font-semibold text-foreground">
              {room.group_description}
            </h4>
            <p className="text-xs text-muted-foreground">
              {room.line_items.length} items
            </p>
          </div>
          <ScopeTableHeader />
          {room.line_items.map((item, lineItemIndex) => (
            <React.Fragment key={`${item.line_number}-${lineItemIndex}`}>
              {/* B-039: bold section headings parsed from the PDF, before this item */}
              {headingsBefore(room.headings, lineItemIndex)}
              <ParseLineItemRow
                item={item}
                onClickUnmapped={
                  onParsedUpdate
                    ? () =>
                        setOverrideTarget({
                          roomIndex,
                          lineItemIndex,
                          description: item.description,
                        })
                    : undefined
                }
              />
            </React.Fragment>
          ))}
          {headingsBefore(room.headings, room.line_items.length)}
        </div>
      ))}

      {overrideTarget && (
        <OverrideModal
          target={overrideTarget}
          onClose={() => setOverrideTarget(null)}
          onSelect={handleSelectOverride}
        />
      )}
    </div>
  );
};

const ParseStatCard: React.FC<{
  label: string;
  value: number;
  color?: string;
}> = ({ label, value, color = 'text-foreground' }) => (
  <div className="rounded-lg border bg-white p-3 text-center shadow-sm">
    <p className={cn('text-2xl font-bold', color)}>{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);

const ACTIVITY_SYMBOLS: Record<string, string> = {
  'Remove and Replace': '&',
  'Replace': '+',
  'Remove': '-',
  // Reason: Detach & Reset is generally "+" (Replace) — sometimes "R"
  // (true detach/reset). Hardcoded to R for now until we wire a rule
  // that picks the right action per item context.
  'Detach & Reset': 'R',
  'Detach and Reset': 'R',
  'Material Only': 'M',
  'Install Only': '+',
  'Labor Only': 'L',
};

const actionSymbol = (activity?: string): string => {
  if (!activity) return '';
  return ACTIVITY_SYMBOLS[activity] || '';
};

const csvEscape = (value: string | number | undefined | null): string => {
  if (value == null) return '';
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const buildCodeListText = (
  estimate: ParsedEstimateResponse['estimate'],
): { text: string; count: number } => {
  const header = ['CAT', 'SEL', 'Action', 'Qty', 'Unit', 'Description'].join('\t');
  const mappedLines: string[] = [];
  const unmappedLines: string[] = [];

  for (const room of estimate.rooms) {
    for (const item of room.line_items) {
      const cols = [
        item.provenance === 'UNMAPPED' ? '—' : item.category,
        item.provenance === 'UNMAPPED' ? '—' : item.selector,
        actionSymbol(item.activity),
        String(item.quantity),
        item.unit,
        item.description,
      ].join('\t');
      if (item.provenance === 'UNMAPPED') {
        unmappedLines.push(cols);
      } else {
        mappedLines.push(cols);
      }
    }
  }

  const parts = [header, ...mappedLines];
  if (unmappedLines.length > 0) {
    parts.push('');
    parts.push('Manual entry required (no code match):');
    parts.push(header);
    parts.push(...unmappedLines);
  }
  return { text: parts.join('\n'), count: mappedLines.length };
};

const buildEstimateCsv = (
  estimate: ParsedEstimateResponse['estimate'],
): string => {
  const header = [
    'Line#',
    'Room',
    'CAT',
    'SEL',
    'Action',
    'Qty',
    'Unit',
    'Description',
    'Provenance',
  ].join(',');
  const rows: string[] = [header];
  for (const room of estimate.rooms) {
    for (const item of room.line_items) {
      const isUnmapped = item.provenance === 'UNMAPPED';
      rows.push(
        [
          csvEscape(item.line_number),
          csvEscape(room.group_description),
          csvEscape(isUnmapped ? '' : item.category),
          csvEscape(isUnmapped ? '' : item.selector),
          csvEscape(actionSymbol(item.activity)),
          csvEscape(item.quantity),
          csvEscape(item.unit),
          csvEscape(item.description),
          csvEscape(item.provenance || ''),
        ].join(','),
      );
    }
  }
  return rows.join('\n');
};

// Reason: shared by both ParseVerificationView (pre-analysis) and RoomSection
// (post-analysis) so the column layout cannot drift between them. The PRP at
// PRPs/verify-parse-useful-output.md specifies a single 8-column layout for
// every line-item table in the completeness UI. Mirrors the col-span layout
// of ScopeLineItemRow (1/1/1/2/1/1/4/1 = 12) below.
export const ScopeTableHeader: React.FC = () => (
  <div className="grid grid-cols-12 gap-2 border-b bg-slate-50 px-4 py-2 text-xs font-medium text-muted-foreground">
    <div className="col-span-1">#</div>
    <div className="col-span-1">CAT</div>
    <div className="col-span-2">SEL</div>
    <div className="col-span-1 text-center">Action</div>
    <div className="col-span-1 text-right">Qty</div>
    <div className="col-span-1">Unit</div>
    <div className="col-span-4">Description</div>
    <div className="col-span-1 text-right">Prov.</div>
  </div>
);

// B-039: a standalone bold section heading parsed from the source PDF
// (e.g. "Mitigation Items", "Tile Floors"). Rendered as a full-width header
// row between line items, mirroring how Xactimate prints a bold/underlined
// heading note above the items it introduces.
export const SectionHeadingRow: React.FC<{ text: string }> = ({ text }) => (
  <div className="border-b bg-slate-100/80 px-4 py-2 text-sm font-semibold text-foreground">
    {text}
  </div>
);

// Render any PDF headings anchored to render BEFORE the line item at `position`.
const headingsBefore = (
  headings: { text: string; position: number }[] | undefined,
  position: number,
): React.ReactNode =>
  (headings || [])
    .filter((h) => h.position === position)
    .map((h, idx) => <SectionHeadingRow key={`h-${position}-${idx}`} text={h.text} />);

interface ScopeLineItemRowProps {
  lineNumber: number;
  // Optional — renders as a blank Action cell when undefined.
  activity?: string;
  category: string;
  selector: string;
  quantity: number | string;
  unit: string;
  description: string;
  provenance?: Provenance;
  provenanceReason?: string;
  isUnmapped?: boolean;
  onClickUnmapped?: () => void;
  // B-039: attached notes parsed from the PDF, rendered as indented sub-rows.
  notes?: string[];
}

export const ScopeLineItemRow: React.FC<ScopeLineItemRowProps> = ({
  lineNumber,
  activity,
  category,
  selector,
  quantity,
  unit,
  description,
  provenance,
  provenanceReason,
  isUnmapped = provenance === 'UNMAPPED',
  onClickUnmapped,
  notes,
}) => {
  const cat = isUnmapped ? '—' : category || '—';
  const sel = isUnmapped ? '—' : selector || '—';
  const showProvenance = provenance && provenance !== 'ORIGINAL';
  const overrideable = isUnmapped && !!onClickUnmapped;
  // B-039: notes are collapsed by default; the row shows a count badge and
  // expands them on click (a real estimate carries long price-list notes, so
  // showing them all inline would bury the line items).
  const noteList = notes ?? [];
  const hasNotes = noteList.length > 0;
  const [notesOpen, setNotesOpen] = useState(false);
  return (
    <>
    <div
      className={cn(
        'grid grid-cols-12 items-center gap-2 border-b px-4 py-2.5 text-sm',
        notesOpen && 'border-b-0',
        isUnmapped ? 'bg-amber-50' : 'hover:bg-slate-50',
        hasNotes && 'cursor-pointer'
      )}
      title={provenanceReason || undefined}
      onClick={hasNotes ? () => setNotesOpen((o) => !o) : undefined}
      role={hasNotes ? 'button' : undefined}
      aria-expanded={hasNotes ? notesOpen : undefined}
    >
      <div className="col-span-1 text-xs text-muted-foreground">{lineNumber}</div>
      <div
        className={cn(
          'col-span-1 font-mono text-xs',
          isUnmapped ? 'text-amber-700' : 'text-primary',
          overrideable && 'cursor-pointer underline-offset-2 hover:underline'
        )}
        onClick={
          overrideable
            ? (e) => {
                e.stopPropagation(); // don't also toggle the notes
                onClickUnmapped?.();
              }
            : undefined
        }
        role={overrideable ? 'button' : undefined}
        tabIndex={overrideable ? 0 : undefined}
        title={overrideable ? 'Click to assign a code' : undefined}
      >
        {cat}
      </div>
      <div
        className={cn(
          'col-span-2 font-mono text-xs',
          isUnmapped ? 'text-amber-700' : 'text-primary'
        )}
      >
        {sel}
      </div>
      <div className="col-span-1 text-center font-mono text-sm text-foreground">
        {actionSymbol(activity)}
      </div>
      <div className="col-span-1 text-right font-mono text-sm">{quantity}</div>
      <div className="col-span-1 text-xs text-muted-foreground">{unit}</div>
      <div className="col-span-4 text-foreground">
        <span>{description}</span>
        {hasNotes && (
          <span
            className="ml-2 inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 align-middle text-[11px] font-medium text-blue-700"
            title={notesOpen ? 'Hide notes' : 'Show notes'}
          >
            <span className="select-none">{notesOpen ? '▾' : '▸'}</span>
            <FileText className="h-3 w-3" />
            {noteList.length} note{noteList.length > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="col-span-1 flex justify-end">
        {showProvenance && <ProvenancePill provenance={provenance} />}
      </div>
    </div>
    {hasNotes && notesOpen && (
      <div className="border-b last:border-b-0 bg-slate-50/40">
        {noteList.map((note, idx) => (
          <div
            key={idx}
            className="flex gap-2 px-4 py-1.5 pl-12 text-xs text-muted-foreground"
          >
            <span className="select-none text-slate-400">↳</span>
            <span className="whitespace-pre-wrap">{note}</span>
          </div>
        ))}
      </div>
    )}
    </>
  );
};

// Reason: post-analysis existing_items expose the code as a combined
// "CAT SEL" string rather than separate fields. Split on the first
// space to feed the shared row component.
const splitXactimateCode = (code: string): { cat: string; sel: string } => {
  const trimmed = (code || '').trim();
  if (!trimmed) return { cat: '', sel: '' };
  const spaceIdx = trimmed.indexOf(' ');
  if (spaceIdx === -1) return { cat: trimmed, sel: '' };
  return {
    cat: trimmed.slice(0, spaceIdx),
    sel: trimmed.slice(spaceIdx + 1),
  };
};

const ParseLineItemRow: React.FC<{
  item: ParsedLineItem;
  onClickUnmapped?: () => void;
}> = ({ item, onClickUnmapped }) => (
  <ScopeLineItemRow
    lineNumber={item.line_number}
    activity={item.activity}
    category={item.category}
    selector={item.selector}
    quantity={item.quantity}
    unit={item.unit}
    description={item.description}
    provenance={item.provenance}
    provenanceReason={item.provenance_reason}
    onClickUnmapped={onClickUnmapped}
    notes={item.notes}
  />
);

const OverrideModal: React.FC<{
  target: OverrideTarget;
  onClose: () => void;
  onSelect: (cat: string, sel: string) => void | Promise<void>;
}> = ({ target, onClose, onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DictionarySearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setError(null);
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const res = await BackendAPI.searchDictionary(q);
        setResults(res.results.slice(0, 10));
        setError(null);
      } catch (err) {
        setError((err as Error).message);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-20"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg border bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 border-b pb-3">
          <div>
            <h4 className="text-base font-semibold text-foreground">
              Assign a code
            </h4>
            <p className="mt-1 text-xs text-muted-foreground">
              <span className="font-mono">{target.description}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search descriptions (e.g., 'metal roofing', 'rain cap')"
          className="mt-3 w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />

        <div className="mt-3 max-h-80 overflow-y-auto">
          {error && (
            <p className="text-sm text-red-700">{error}</p>
          )}
          {!error && query.trim().length < 2 && (
            <p className="text-sm text-muted-foreground">
              Type at least 2 characters to search.
            </p>
          )}
          {!error && searching && (
            <p className="text-sm text-muted-foreground">Searching…</p>
          )}
          {!error && !searching && query.trim().length >= 2 && results.length === 0 && (
            <p className="text-sm text-muted-foreground">No matches.</p>
          )}
          <ul className="space-y-1">
            {results.map((r) => (
              <li key={`${r.cat}-${r.sel}-${r.activity_code}-${r.id ?? ''}`}>
                <button
                  type="button"
                  className="w-full rounded-md border border-transparent px-3 py-2 text-left text-sm hover:border-primary hover:bg-slate-50"
                  onClick={() => onSelect(r.cat, r.sel)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-primary">
                      {r.cat} {r.sel}
                    </span>
                    <span className="text-xs text-muted-foreground">{r.unit}</span>
                  </div>
                  <p className="mt-0.5 text-sm text-foreground">{r.description}</p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export const ProvenancePill: React.FC<{ provenance?: string; label?: string }> = ({
  provenance,
  label,
}) => {
  if (!provenance) return null;
  const style: Record<string, string> = {
    ORIGINAL: 'bg-green-100 text-green-800',
    UNMAPPED: 'bg-amber-100 text-amber-800',
    ADDED: 'bg-blue-100 text-blue-800',
    CHANGED: 'bg-purple-100 text-purple-800',
    UNCHANGED: 'bg-slate-100 text-slate-700',
    MANUAL: 'bg-teal-100 text-teal-800',
  };
  const icon: Record<string, React.ReactNode> = {
    UNMAPPED: <AlertTriangle className="h-3 w-3" />,
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        style[provenance] || 'bg-slate-100 text-slate-700'
      )}
      title={label}
    >
      {icon[provenance]}
      {provenance.charAt(0) + provenance.slice(1).toLowerCase()}
    </span>
  );
};
